import {DropTargetMonitor, useDrop} from "react-dnd";
import {IItem} from "../../interfaces/Item";
import React, {useEffect, useRef} from "react";
import {findDOMNode} from "react-dom";
import {BARCODE, SIMPLE_TEXT, VARIABLE_TEXT, PICTURE, VARIABLE_PICTURE, VERTICAL_LINE, HORIZONTAL_LINE} from "../../utils/ItemTypes";
import {useSelector} from "react-redux";
import {RootState} from "../../app/store";
import {useAppDispatch} from "../../app/hooks";
import {AddItemToLabel, SelectItem} from "../MainActions";
import {MoveableObject} from "./MoveableObject";
import {ChangeBarcodeValNoHist, GenerateBarcode} from "../BarcodeActions";
import {defaultBarcode} from "../../utils/defaultBarcode";
import {getBarcodeDefaultValue} from "../../utils/barcodeUtils";
import localforage from "localforage";
import GridLines from "react-gridlines";

interface Props {
  zoom: number;
  setZoom: (zoom: number) => void;
}

export const Label = ({zoom, setZoom}: Props) => {
  const renderRef = useRef<any>();
  const dispatch = useAppDispatch();

  const {labelItems, labelParams} = useSelector((state: RootState) => state.labelEdit.present);
  const {barcodeTags} = useSelector((state: RootState) => state.refData);

  const labelZoomRef = useRef(labelParams.zoom); 
  useEffect(() => {
    labelZoomRef.current = labelParams.zoom; 
  }, [labelParams.zoom]);

  const [{isOver}, drop] = useDrop(() => ({
    accept: [SIMPLE_TEXT, BARCODE, VARIABLE_TEXT, PICTURE, VARIABLE_PICTURE, VERTICAL_LINE, HORIZONTAL_LINE],
    drop: async (item: IItem, monitor: DropTargetMonitor) => {
      if (!monitor || !renderRef.current) {
        return;
      }

      let offset = monitor.getSourceClientOffset();

      if (!offset) {
        offset = monitor.getClientOffset();
      }

      if (!offset) {
        return;
      }

      const componentRect = (findDOMNode(renderRef.current) as HTMLElement)!.getBoundingClientRect();
      const currentZoom = labelZoomRef.current;
      // let x = ((offset?.x || 0) - spacing - componentRect.left) / zoom;
      let x = ((offset?.x || 0) - componentRect.left) / currentZoom;
      // let x = ((offset?.x || 0) - spacing - componentRect.left) / zoom;
      let y = ((offset?.y || 0) - componentRect.top) / currentZoom;

      item.width = 20;
      item.xInMM = x * (1 / labelParams.pxInMM); //convert to MM
      item.yInMM = y * (1 / labelParams.pxInMM); // convert to MM
      if (item.type === HORIZONTAL_LINE) {
        item.yToInMM = y * (1 / labelParams.pxInMM);
        item.xToInMM = item.xInMM + item.width;
      }
      if (item.type === VERTICAL_LINE) {
        item.xToInMM = x * (1 / labelParams.pxInMM);
        item.yToInMM = item.yInMM + item.width;
      }
      item.fontSize = 12;
      item.style = {
        position: "absolute",
        top: `${y}px`,
        left: `${x}px`,
      };
      item.itemid = Math.floor(Number((labelItems.length + 1).toString() + (Math.random() * 100000000).toString()));

      if (item.type === BARCODE) {
        const barcodeUrl = await dispatch(GenerateBarcode(item.itemid));
        if (barcodeUrl) {
          item.value = barcodeUrl;
        }
        item.width = defaultBarcode.Width;
        item.height = defaultBarcode.Height;
        item.name = defaultBarcode.Name;
        item.showCaption = Boolean(defaultBarcode.ShowCaption);
        item.captionSize = defaultBarcode.CaptionSize;
        item.barcodeType = defaultBarcode.Type;
        item.defaultText = getBarcodeDefaultValue(barcodeTags, defaultBarcode.Name);
      }

      dispatch(AddItemToLabel(item)).then(() => {
        dispatch(SelectItem(item));
        setZoom(1);
      });
    },
    collect: (monitor) => ({
      isOver: !!monitor.isOver(),
      canDrop: !!monitor.canDrop(),
    }),
  }));

  drop(renderRef);

  const labelstyle = {
    backgroundColor: "white",
    border: isOver ? "5px solid #31a6fd" : "none",
    overflow: "hidden",
    top: 30,
    left: 30,
  };

  useEffect(() => {
    // console.log('[Label] loadImages useEffect - labelItems count:', labelItems?.length);
    if (!labelItems) return;
    
    const createdUrls: string[] = [];
    
    const loadImages = async () => {
      for (const item of labelItems) {
        if (item.type === BARCODE || item.type === PICTURE || item.type === VARIABLE_PICTURE) {
          try {
            const value: any = await localforage.getItem(item.itemid.toString());
            if (value) {
              const blobUrl = URL.createObjectURL(value);
              createdUrls.push(blobUrl);
              dispatch(ChangeBarcodeValNoHist(item, "value", blobUrl));
            }
          } catch (error) {
            console.error('Failed to load image from storage:', error);
          }
        }
      }
    };
    
    loadImages();
    
    // cleanup blobs on unmount
    return () => {
      // console.log('[Label] Cleaning up blob URLs:', createdUrls.length);
      createdUrls.forEach(url => URL.revokeObjectURL(url));
    };
  }, [dispatch]);
  return (
    <div style={{...labelstyle, position: "absolute", height: labelParams.heightInPx, width: labelParams.widthInPx}} ref={renderRef}>
      {labelItems && labelItems.map((item: IItem, idx: number) => item && <MoveableObject key={idx} item={item} zoom={zoom} setZoom={setZoom} />)}
      {labelParams.showGrid && (
        <GridLines
          className="grid-area"
          strokeWidth={2}
          strokeWidth2={0.5}
          cellWidth={labelParams.pxInMM * (labelParams.gridCellWidth ? labelParams.gridCellWidth : 10)}
          cellWidth2={labelParams.pxInMM * (labelParams.gridCellWidth ? labelParams.gridCellWidth : 10) * 0.5}
        ></GridLines>
      )}
    </div>
  );
};
